import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ResetPasswordRequestInput,
  ResetPasswordRequestSchema,
  ResetPasswordSchema,
} from '@simplemon/lib';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestPasswordReset, resetPassword } from '@/lib/auth-client';

const ResetPasswordFormSchema = z
  .object({
    password: ResetPasswordSchema.shape.password,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormInput = z.infer<typeof ResetPasswordFormSchema>;

type AuthResultError = {
  code?: string;
  message?: string;
};

function getAuthResultError(result: unknown): AuthResultError | null {
  if (typeof result !== 'object' || result === null || !('error' in result)) {
    return null;
  }

  if (typeof result.error === 'string') {
    return { message: result.error };
  }

  if (typeof result.error !== 'object' || result.error === null) {
    return null;
  }

  const error = result.error as Record<string, unknown>;
  return {
    code: typeof error.code === 'string' ? error.code : undefined,
    message: typeof error.message === 'string' ? error.message : undefined,
  };
}

function getResetCompletionErrorMessage(error: AuthResultError): string {
  const code = error.code?.toUpperCase();
  const message = error.message?.toLowerCase();

  if (
    code === 'INVALID_TOKEN' ||
    code === 'TOKEN_EXPIRED' ||
    message?.includes('invalid token') ||
    message?.includes('expired token')
  ) {
    return 'This reset link is invalid or expired. Please request a new one.';
  }

  return error.message || 'Unable to update your password. Please try again.';
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [errorParam, setErrorParam] = useState<string | null>(null);
  const tokenParam = useMemo(() => searchParams.get('token'), [searchParams]);
  const errorQuery = useMemo(() => searchParams.get('error'), [searchParams]);

  useEffect(() => {
    if (errorQuery) {
      setErrorParam(errorQuery);
      setResetToken(null);
      sessionStorage.removeItem('resetPasswordToken');
    }

    if (tokenParam) {
      setResetToken(tokenParam);
      setErrorParam(null);
      sessionStorage.setItem('resetPasswordToken', tokenParam);
    }

    if (!tokenParam && !errorQuery) {
      const storedToken = sessionStorage.getItem('resetPasswordToken');
      if (storedToken) {
        setResetToken(storedToken);
      }
    }

    if (tokenParam || errorQuery) {
      navigate('/reset-password', { replace: true });
    }
  }, [errorQuery, navigate, tokenParam]);

  const showResetForm = !!resetToken;
  const errorMessage =
    errorParam === 'INVALID_TOKEN'
      ? 'This reset link is invalid or expired. Please request a new one.'
      : 'Reset link error. Please request a new one.';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Valvo.io</h1>
          <p className="mt-2 text-sm text-slate-600">Manage your account access</p>
        </div>
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-semibold text-center">
              {showResetForm ? 'Set a new password' : 'Reset your password'}
            </CardTitle>
            <CardDescription className="text-center">
              {showResetForm
                ? 'Choose a new password for your account'
                : 'We will send you a reset link if the email exists'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {errorParam && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            {showResetForm ? (
              <ResetPasswordForm
                token={resetToken ?? ''}
                onComplete={() => {
                  sessionStorage.removeItem('resetPasswordToken');
                }}
              />
            ) : (
              <ResetPasswordRequestForm onResetRequest={() => setErrorParam(null)} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ResetPasswordRequestForm({ onResetRequest }: { onResetRequest?: () => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordRequestInput>({
    resolver: zodResolver(ResetPasswordRequestSchema),
  });

  const onSubmit = async (data: ResetPasswordRequestInput) => {
    onResetRequest?.();
    setIsLoading(true);
    setError(null);

    try {
      const result = await requestPasswordReset({ email: data.email });
      const resultError = getAuthResultError(result);
      if (resultError) {
        setError(resultError.message || 'Unable to request a reset link. Please try again.');
        return;
      }

      setSuccess('If that email exists, we sent a reset link. Check your inbox.');
    } catch (err) {
      console.error('Reset password request error:', err);
      setError('Operation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@company.com"
          className="h-11"
          {...register('email')}
          disabled={isLoading}
          required
        />
        {errors.email && (
          <p className="text-sm font-medium text-destructive">{errors.email.message}</p>
        )}
      </div>

      {success && (
        <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full h-11 text-base" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          'Send reset link'
        )}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        Remembered your password?{' '}
        <Link to="/login" className="font-semibold text-primary hover:text-primary/80">
          Sign in
        </Link>
      </p>
    </form>
  );
}

function ResetPasswordForm({ token, onComplete }: { token: string; onComplete?: () => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormInput>({
    resolver: zodResolver(ResetPasswordFormSchema),
  });

  const onSubmit = async (data: ResetPasswordFormInput) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await resetPassword({ newPassword: data.password, token });
      const resultError = getAuthResultError(result);
      if (resultError) {
        setError(getResetCompletionErrorMessage(resultError));
        return;
      }

      setSuccess('Password updated. You can now sign in.');
      onComplete?.();
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Operation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="password">New password</Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          className="h-11"
          autoComplete="off"
          {...register('password')}
          disabled={isLoading || !!success}
          required
        />
        {errors.password && (
          <p className="text-sm font-medium text-destructive">{errors.password.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm password</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="••••••••"
          className="h-11"
          autoComplete="off"
          {...register('confirmPassword')}
          disabled={isLoading || !!success}
          required
        />
        {errors.confirmPassword && (
          <p className="text-sm font-medium text-destructive">{errors.confirmPassword.message}</p>
        )}
      </div>

      {success && (
        <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full h-11 text-base" disabled={isLoading || !!success}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Updating...
          </>
        ) : (
          'Update password'
        )}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        Back to{' '}
        <Link to="/login" className="font-semibold text-primary hover:text-primary/80">
          Sign in
        </Link>
      </p>
    </form>
  );
}
